import { Injectable } from '@angular/core';

import {
  CreateOrderCommand,
  GetOrderByAccessIdCommand,
  OrderItemCreate,
  OrderOutput,
  PropertyData,
  SendPOAEMailCommand,
  UpdateOrderDataCommand,
  UserData,
} from 'frontend.client.api';
import { ApiService } from '../../shared/api.service';
import { PartnerStateService } from '../../shared/partner-state.service';
import { RouterService } from '../../shared/router.service';
import { ThresholdService } from '../../shared/threshold.service';
import { TokenService } from '../../shared/token.service';
import { AccessHashService } from '../access-hash.service';
import { OrderServiceUtils } from './order.service.utils';
import { AccessIdService } from '../access-id.service';

export interface ChangeableOrder {
  items?: OrderItemCreate[];
  userData?: UserData;
  propertyData?: PropertyData;
}
@Injectable({
  providedIn: 'root',
})
export class OrderServiceAPI extends OrderServiceUtils {
  constructor(
    protected apiService: ApiService,
    protected partnerStateService: PartnerStateService,
    protected router: RouterService,
    protected accessHashService: AccessHashService,
    protected thresholdService: ThresholdService,
    protected tokenService: TokenService,
    protected accessIdService: AccessIdService
  ) {
    super(
      apiService,
      partnerStateService,
      router,
      accessHashService,
      thresholdService,
    );
  }

  public handleUpdatedOrderOutput(orderOutput?: OrderOutput) {
    this.orderState$.next({
      order: orderOutput,
      isLoading: false,
      error: undefined,
    });
  }

  protected async createOrderWithoutSettingState() {
    if (!this.partnerStateService.partner?.partnerId) return;
    try {
      this.orderState$.next({
        order: this.orderState$.getValue().order,
        isLoading: true,
        error: undefined,
      });
      const command = new CreateOrderCommand({
        partnerId: this.partnerStateService.partner?.partnerId,
        items: [],
      });
      const createdOrder = await this.apiService.client?.send(command);
      this.tokenService.setToken(createdOrder?.accessToken ?? '');
      this.accessIdService.setAccessId(createdOrder?.accessId);
      return createdOrder;
    } catch (e) {
      if (e instanceof Error) {
        this.orderState$.next({
          order: this.orderState$.getValue().order,
          isLoading: false,
          error: e,
        });
      }
      return;
    }
  }

  protected async createOrder(items: OrderItemCreate[]) {
    if (!this.partnerStateService.partner?.partnerId) return;

    try {
      this.orderState$.next({
        order: this.orderState$.getValue().order,
        isLoading: true,
        error: undefined,
      });
      const command = new CreateOrderCommand({
        partnerId: this.partnerStateService.partner?.partnerId,
        items,
      });
      const createdOrder = await this.apiService.client?.send(command);
      this.tokenService.setToken(createdOrder?.accessToken ?? '');
      this.accessIdService.setAccessId(createdOrder?.accessId);
      this.handleUpdatedOrderOutput(createdOrder);
      return createdOrder;
    } catch (e) {
      if (e instanceof Error) {
        this.orderState$.next({
          order: this.orderState$.getValue().order,
          isLoading: false,
          error: e,
        });
      }
      return;
    }
  }

  protected async updateOrder(order: {
    items?: OrderItemCreate[];
    userData?: UserData;
    propertyData?: PropertyData;
  }) {
    const { items, userData, propertyData } = order;
    const currentOrder = this.orderState.getValue().order;

    if (!currentOrder || !currentOrder?.orderId)
      throw new Error('No Order found');

    try {
      this.orderState$.next({
        order: currentOrder,
        isLoading: true,
        error: undefined,
      });
      const command = new UpdateOrderDataCommand({
        ...currentOrder,
        items:
          items ?? this.orderItemToOrderItemCreate(currentOrder.items),
        propertyData: this.patchPropertyData(
          currentOrder.propertyData,
          propertyData,
        ),
        userData: this.patchUserData(currentOrder.userData, userData),
      });
      this.apiService.addMiddleware(command);
      await this.apiService.client?.send(command);
  
    } catch (e) {
      if (e instanceof Error) {
        console.error(e);
        this.orderState$.next({
          order: this.orderState$.getValue().order,
          isLoading: false,
          error: e,
        });
      }
      return;
    }
  }

  protected async syncLocalToRemote() {
    const localOrder = this.orderState$.getValue().order;
    if (!localOrder) return;
    this.updateOrder({
      items: this.orderItemToOrderItemCreate(localOrder.items),
      userData: localOrder.userData,
      propertyData: localOrder.propertyData,
    });
  }

  protected async getOrderByAccessIDWithoutUpdatingState(): Promise<
    OrderOutput | undefined
  > {
    const id = this.accessIdService.currentAccessId();
    if (!id) return;
    if (!this.partnerStateService) return;
    const command = new GetOrderByAccessIdCommand({
      accessId: id,
    });
    this.apiService.addMiddleware(command);
    const order = await this.apiService.client?.send(command);
    return order;
  }

  protected async getOrderByAccessID() {

    const id = this.accessIdService.currentAccessId();
    const partner = this.partnerStateService.partnerState.getValue().partnerData;
    if (!id) return;
    if (!partner) return;
    const command = new GetOrderByAccessIdCommand({
      accessId: id,
    });
    try {
      this.apiService.addMiddleware(command);
      const order = await this.apiService.client?.send(command);

      if (order) {
        const localOrder = this.orderState$.getValue().order;
        if (localOrder && localOrder.orderId === order.orderId) {
          // Make sure we don't overwrite data in the local order if it's not present in the remote order
          const patched: OrderOutput = {
            ...order,
            items: localOrder.items ?? order.items,
            propertyData: this.patchPropertyData(
              order.propertyData,
              localOrder.propertyData,
            ),
            userData: this.patchUserData(order.userData, localOrder.userData),
          };
          this.handleUpdatedOrderOutput(patched);
        } else {
          this.handleUpdatedOrderOutput(order);
        }
      }
      this.orderLockState$.next(false);
      return order;
    } catch (e) {
      throw e;
    }
  }
}
